import { Box, Grid, InputAdornment } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Search, Work, WorkOutline } from '@material-ui/icons';
import { InfiniteScrollGrid } from '@roc/feature-app-core';
import { debounce } from '@roc/feature-utils';
import { DataGrid, filterProps, SelectMode, TextField } from '@roc/ui';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { SelectSide } from '@roc/ui';
import { CompanyProfileStore } from '../../stores/companyProfileStore';

const useStyles = makeStyles(theme => ({
  container: {
    ['& .ag-cell-value, & .ag-selection-checkbox']: {
      alignSelf: 'flex-start',
    },
  },
  button: {
    width: '358px',
    margin: '0 auto',
    boxShadow:
      '0px 3px 1px -2px rgba(0, 0, 0, 0.2),' +
      '0px 2px 2px 0px rgba(0, 0, 0, 0.14),' +
      '0px 1px 5px 0px rgba(0, 0, 0, 0.12)',
  },
}));

export enum SelectEntityOption {
  SEARCH_ENTITY = 'SEARCH_ENTITY',
  ADD_ENTITY = 'ADD_ENTITY',
  NO_ENTITY = 'NO_ENTITY',
}

interface SearchBorrowersForPricingEconomicsTierProps {
  companyProfileStore: CompanyProfileStore
}

export const SearchBorrowersForPricingEconomicsTier = observer((props: SearchBorrowersForPricingEconomicsTierProps) => {
  const classes = useStyles();
  const { companyProfileStore } = props
  const [searchValue, setSearchValue] = useState('');
  const [gridApi, setGridApi] = useState(null);

  const onFilterFullName = (value) => {
    setSearchValue(value);
    const filterInstance = gridApi.getFilterInstance('fullName');
    filterInstance.setModel({
      type: 'contains',
      filter: value,
    });
    gridApi.onFilterChanged();
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const handleModelUpdate = (params) => {
    const rowCount = params.api.getDisplayedRowCount();

    if (companyProfileStore.lenderTermTierEconomicsFormStore.termTierToDelete !== null) {
      const termTierList = companyProfileStore.lenderTermTierEconomicsFormStore.termTierList;

      const matchingTier = termTierList.find(
        (tier) => tier.submissionPricingTiers.tierId === companyProfileStore.lenderTermTierEconomicsFormStore.termTierToDelete
      );

      const borrowerIds = matchingTier.lenderBorrowers.map(b => b.borrowerId);

      for (let i = 0; i < rowCount; i++) {
        const rowNode = params.api.getDisplayedRowAtIndex(i);
        const borrowerId = rowNode.data?.borrowerId;
        if (borrowerIds.includes(borrowerId)) {
          rowNode.setSelected(true);
        }
      }
    }
  }

  const columns = [
    {
      field: 'fullName',
      headerName: 'Borrower Name',
      valueGetter: params => {
        const borrowerNameAndLastName = `${params?.data?.firstName} ${params?.data?.lastName}`
        return borrowerNameAndLastName;
      },
      ...filterProps(),
    },
    {
      field: 'emailAddress',
      headerName: 'Borrower Email',
      ...filterProps(),
    },
    {
      field: 'medianScore',
      headerName: 'Credit',
      ...filterProps(),
    },
  ];

  const debounced = debounce(fn => fn(), 10);

  const frameworkComponents = {
    borrowerNameCellRenderer: params => {
      const borrowers = params.node.data?.borrowers ?? [];
      params.node.setRowHeight(6 + borrowers.length * 40);
      debounced(() => params.api.onRowHeightChanged());
      return (
        <Box>
          {borrowers.map(borrower => (
            <Box>{borrower.borrowerName}</Box>
          ))}
        </Box>
      );
    }
  };

  const onRowSelected = params => {
    if (params.node.isSelected()) {
      companyProfileStore.lenderTermTierEconomicsFormStore.addBorrower(params.node.data)
    } else {
      companyProfileStore.lenderTermTierEconomicsFormStore.deleteBorrower(params.node.data.borrowerId)
    }
  };

  return (
    <>
      <Grid container spacing={2} className={classes.container}>
        <Grid item xs={12}>
          <TextField
            testId="search-text"
            value={searchValue}
            fullWidth
            variant="outlined"
            placeholder="Search Existing Borrowers by First or Last Name"
            onChange={e =>
              onFilterFullName(e.target.value)
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid style={{ height: '375px' }} container item xs={12}>
          <InfiniteScrollGrid
            columns={columns}
            selectMode={SelectMode.MULTIPLE}
            frameworkComponents={frameworkComponents}
            handleModelUpdated={handleModelUpdate}
            gridReady={onGridReady}
            suppressRowClickSelection={true}
            onRowSelected={onRowSelected}
            selectSide={SelectSide.LEFT}
            getRowNodeId={(params) => params.borrowerId}
            store={props.companyProfileStore.lenderTermTierEconomicsFormStore.possibleTierBorrowers} />
        </Grid>
      </Grid>
    </>
  );
});
